import "./style.css"

import {
    ACESFilmicToneMapping,
    BoxBufferGeometry, Color, Shape, Vector3,
    EquirectangularReflectionMapping, ExtrudeGeometry,
    Mesh,
    MeshPhysicalMaterial,
    PerspectiveCamera,
    Scene,
    sRGBEncoding,
    TextureLoader,
    WebGLRenderer, Group
} from "three";

const TAU = Math.PI * 2;
import QuickHull from "quickhull"

import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { BokehPass } from "three/examples/jsm/postprocessing/BokehPass.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls"


let camera, scene, renderer, stats, shardGroup;

let mouseX = 0, mouseY = 0;

let windowHalfX = window.innerWidth / 2;
let windowHalfY = window.innerHeight / 2;

let width = window.innerWidth;
let height = window.innerHeight;

let controls;

let materials = [];

let shards = [];

const postprocessing = {};

let textureEquirec;

const up = new Vector3(0,1,0)

class Shard {
    /**
     *
     * @type {Mesh}
     */
    mesh = null;

    /**
     *
     * @type {Vector3}
     */
    direction = null

    constructor(mesh, size, rot)
    {
        this.mesh = mesh;

        let dir = this.mesh.position.clone().normalize().applyAxisAngle(up, rot)
        this.direction = dir.multiplyScalar( (1 + Math.random()) * 0.05 / Math.pow(size,0.5) );
    }

}


function createShard()
{
    const pad = 2;
    const size = 100;

    const rnd = Math.random();

    const angle = TAU * Math.random();
    const rnd2 = Math.random()
    const r = 0 | size * Math.pow(rnd2, 2) + size/20;

    const bx = Math.cos(angle) * r;
    const by = Math.sin(angle) * r;

    const h = 0 | size/50 + size/4 * rnd * rnd * rnd;
    const span = h * (1 + Math.random())/(TAU * r);

    const count = 5;

    const hCount = count/2;

    const pts = [];
    const spanStep = span / count;
    const hStep = h/count;
    for (let y = 0; y < count; y++)
    {
        for (let x = -hCount; x < hCount; x++)
        {
            if (Math.random() < 0.3)
            {
                const a2 = angle + spanStep * x;
                const radius = r + y * hStep;

                const x1 =  Math.cos(a2) * radius;
                const y1 =  Math.sin(a2) * radius;

                // dull the edges

                if (Math.random() < 0.7)
                {
                    pts.push({
                        x: x1 + pad,
                        y: y1
                    }, {
                        x: x1 - pad,
                        y: y1
                    }, {
                        x : x1,
                        y: y1 + pad,
                    }, {
                        x : x1,
                        y: y1 - pad
                    })
                }
                else
                {
                    pts.push({
                        x: x1,
                        y: y1
                    })
                }
            }
        }
    }

    const hull = QuickHull(pts);


    const shape = new Shape();
    shape.moveTo( hull[0].x,hull[0].y );

    for (let i = 1; i < hull.length; i++)
    {
        const { x, y } = hull[i];
        shape.lineTo( x,y );

    }
    shape.lineTo( hull[0].x,hull[0].y );

    const extrudeSettings = {
        steps: 1,
        depth: 0.2,
        bevelEnabled: false
    };

    const geometry = new ExtrudeGeometry( shape, extrudeSettings );
    const material = materials[0 | Math.random() * materials.length];
    let mesh = new Mesh( geometry, material );

    const ySpread = TAU/4;

    mesh.position.x = bx;
    mesh.position.y = by;
    const rot = Math.random() * ySpread;
    mesh.rotation.y = rot;

    return new Shard(mesh, h, rot);
}


function createRandomPalette(params)
{
    const createMaterial = color =>
        new MeshPhysicalMaterial({
            ...params,
            envMap: textureEquirec,
            color
        })

    const baseHue = 0| Math.random() * 360;
    const otherHue = (baseHue + 180) % 360;

    const l0 = baseHue >= 30 && baseHue < 210 ? 40 : 60
    const l1 = otherHue >= 30 && otherHue < 210 ? 40 : 60

    materials = [
        createMaterial(new Color("#000")),
        createMaterial(new Color(`hsl(${baseHue}, 100%, ${l0}%)`)),
        createMaterial(new Color(`hsl(${otherHue}, 100%, ${l1}%)`)),
        createMaterial(new Color("#fff")),
    ]

}


function createScene(params)
{
    createRandomPalette(params)

    const group = new Group()

    const numBlobs = 800 + 700 * Math.random();

    for (let i = 0; i < numBlobs; i++)
    {
        const shard = createShard();
        shards.push(shard)
        group.add(shard.mesh)
    }

    return group;
}

function destroy()
{
    for (let i = 0; i < shards.length; i++)
    {
        const shard = shards[i];
        scene.remove(shard.m)
    }
}



const materialParams = {
    roughness: 0,
    transmission: 0.7,
    ior: 1.35,
    thickness: 0.01,
    specularIntensity: 1,
    specularColor: 0xffffff,
    envMapIntensity: 1.5,
    opacity: 0.7,
    transparent: true

}



function init() {

    const container = document.getElementById( 'screen' );

    camera = new PerspectiveCamera( 70, width / height, 0.01, 3000 );

    setRandomCamera();

    scene = new Scene();

    renderer = new WebGLRenderer();
    renderer.setPixelRatio( window.devicePixelRatio );
    renderer.setSize( width, height );
    container.appendChild( renderer.domElement );

    // https://github.com/mrdoob/three.js/blob/master/examples/webgl_materials_physical_transmission.html

    const textureLoader = new TextureLoader();

    textureEquirec = textureLoader.load( 'media/smooth-bg.jpg' );
    textureEquirec.mapping = EquirectangularReflectionMapping;
    //textureEquirec.encoding = sRGBEncoding;

    scene.background = textureEquirec

    shardGroup = createScene(materialParams);
    scene.add(shardGroup);
    // {
    //     const cubeMaterial = new MeshPhysicalMaterial( {
    //         ... materialParams,
    //         color: 0x000000
    //     });
    //
    //     const geo = new BoxBufferGeometry(1,1,0.1)
    //     const mesh = new Mesh(geo, cubeMaterial)
    //     scene.add(mesh)
    // }
    //
    // {
    //     const cubeMaterial = new MeshPhysicalMaterial( {
    //         ... materialParams,
    //         color: 0xffcc00
    //     });
    //
    //     const geo = new BoxBufferGeometry(1,1,0.1)
    //     const mesh = new Mesh(geo, cubeMaterial)
    //     mesh.position.x = -0.5;
    //     mesh.position.z = -0.5;
    //     scene.add(mesh)
    // }


    initPostprocessing();

    renderer.autoClear = false;

    //stats = new Stats();
    //container.appendChild( stats.dom );

    window.addEventListener( 'resize', onWindowResize );

    controls = new OrbitControls(camera, renderer.domElement);
    //controls.maxPolarAngle = Math.PI * 0.45;
    controls.maxPolarAngle = Math.PI;
    controls.target.set(0, 0, 0);
    controls.minDistance = 0.2;
    controls.maxDistance = 100.0;
    controls.enableDamping = true;
    controls.dampingFactor = 0.02;
    controls.update();


}

function onWindowResize() {

    windowHalfX = window.innerWidth / 2;
    windowHalfY = window.innerHeight / 2;

    width = window.innerWidth;
    height = window.innerHeight;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    renderer.setSize( width, height );

    renderer.toneMapping = ACESFilmicToneMapping;
    renderer.outputEncoding = sRGBEncoding;

    postprocessing.composer.setSize( width, height );

}

function initPostprocessing() {

    const renderPass = new RenderPass( scene, camera );

    // const bokehPass = new BokehPass( scene, camera, {
    //     focus: 1.0,
    //     aperture: 0.015,
    //     maxblur: 0.005,
    //
    //     width: width,
    //     height: height
    // } );

    const composer = new EffectComposer( renderer );

    composer.addPass( renderPass );
    //composer.addPass( bokehPass );

    postprocessing.composer = composer;
    //postprocessing.bokeh = bokehPass;

}

let counter = 0;
let maxCounter = 1000;
let fadeoutSpeed = 0.005;
let fadeoutLimit = (maxCounter - 1/fadeoutSpeed) | 0;

let opacity = 1;


function setRandomCamera()
{
    const angleSpread = TAU / 6;
    const cameraAngle = TAU / 4 - angleSpread / 2 + Math.random() * angleSpread

    camera.position.x = Math.cos(cameraAngle) * 100;
    camera.position.y = 0 | Math.random() * 10 + 10;
    camera.position.z = Math.sin(cameraAngle) * 100;
    camera.updateProjectionMatrix();

}


function animate() {


    //stats.begin();
    const time = Date.now() * 0.00005;

    for (let i = 0; i < shards.length; i++)
    {
        const shard = shards[i];
        shard.mesh.position.add(shard.direction)
    }

    postprocessing.composer.render(0.1);
    //stats.end();

    controls.update()

    counter++
    if (counter >= fadeoutLimit && counter < maxCounter)
    {
        opacity -= 0.01;
    }
    if (counter >= maxCounter)
    {
        counter = 0;
        scene.remove(shardGroup);
        shardGroup = createScene(materialParams);
        scene.add(shardGroup);
        setRandomCamera();


        setTimeout(
            () => opacity = 1,
            20
        )
    }
    renderer.domElement.style.opacity = opacity;
    requestAnimationFrame(animate);
}


init();
requestAnimationFrame(animate);
